import {
  Box,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import {
  Control,
  Controller,
  FieldArrayWithId,
  FieldErrors,
  UseFormGetValues,
  UseFormWatch,
} from "react-hook-form";
import { FormData } from "../../constants";
import InfoBox from "src/shared/components/info-box";
import { useEffect } from "react";
import YoutubeViewerBox from "../../youtube-viewer-box";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTranslation } from "react-i18next";
import { formatNumber } from "src/utils";
import { FormModeTypes } from "src/types/generic";

const MakeBalance = ({
  balanceFields,
  control,
  errors,
  getValues,
  journeyData,
  watch,
  balanceAmountMatch,
  setBalanceAmountMatch,
  balanceQntyMatch,
  setBalanceQntyMatch,
  isOnboarding,
  mode,
}: {
  balanceFields: FieldArrayWithId<FormData, "balance", "id">[];
  control: Control<FormData, any>;
  errors: FieldErrors<FormData>;
  getValues: UseFormGetValues<FormData>;
  journeyData: any;
  watch: UseFormWatch<FormData>;
  balanceAmountMatch: boolean;
  setBalanceAmountMatch: any;
  balanceQntyMatch: boolean;
  setBalanceQntyMatch: Function;
  isOnboarding: boolean;
  mode: FormModeTypes;
}) => {
  const theme = useTheme();
  const matchesSMUp = useMediaQuery(theme.breakpoints.up("sm"));
  const { t } = useTranslation("addSales");
  const { t: settingsT } = useTranslation("settings");
  const { t: generalT } = useTranslation("general");

  const accumulatedBalance = watch("balance")?.reduce(
    (acc: { amount: number; quantity: number }, payment: any) => {
      acc.amount += +payment.amount;
      acc.quantity += +payment.quantity;
      return acc;
    },
    { amount: 0, quantity: 0 }
  );

  const netSalesWithTax =
    watch("journey_type") === "manual"
      ? +watch("net_sales_with_tax")
      : journeyData?.sale?.gross_sales;

  const totalQuantity =
    watch("journey_type") === "manual"
      ? +watch("total_order_quantity")
      : journeyData?.payments.reduce(
          (sum: number, payment: any) => sum + payment.quantity,
          0
        );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Prevents entering negative sign or other invalid characters
    if (event.key === "-" || event.key === "e") {
      event.preventDefault(); // Disables negative sign and scientific notation
    }
  };

  useEffect(() => {
    if (
      accumulatedBalance?.amount.toFixed(2) === totalNetSalesWithTax.toFixed(2)
    ) {
      setBalanceAmountMatch(true);
    }
  }, [accumulatedBalance]);

  useEffect(() => {
    if (accumulatedBalance?.quantity >= totalQuantity) {
      setBalanceQntyMatch(true);
    }
  }, [accumulatedBalance]);

  const isOneDayFoodicsJourney =
    journeyData && journeyData.start_date === journeyData.end_date;

  const totalNetSalesWithTax = +netSalesWithTax;

  return (
    <Box>
      <Typography fontSize={"26px"} fontWeight={600}>
        {t("nowYouCanMakeBalance")}
      </Typography>
      <Typography color={"textSecondary"}>{t("checkFollowing")}</Typography>

      <YoutubeViewerBox videoId="4E4B9g-zQP8" />

      {!isOneDayFoodicsJourney && watch("journey_type") === "foodics" && (
        <Box mb={2}>
          <InfoBox text={t("EditBalanceOnly")} />
        </Box>
      )}

      <Box borderRadius={"8px"} overflow={"auto"} maxWidth={"100%"}>
        <Grid container spacing={2} bgcolor={"#F9FAFB"} p={1.5}>
          <Grid item xs={3}>
            <Typography flexBasis={"50%"}>
              {generalT("paymentMethod")}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography flexBasis={"50%"}>{generalT("quantity")}</Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography flexBasis={"50%"}>
              {t("paymentAmount")} ({generalT("sar")})
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography flexBasis={"50%"}>
              {t("startBalance")} ({generalT("sar")})
            </Typography>
          </Grid>
        </Grid>
        <Box
          borderRadius={"0 0 0 8px"}
          borderRight={"1px solid #EAECF0"}
          borderLeft={"1px solid #EAECF0"}
        >
          {balanceFields.map((balanceField, index: number) => (
            <Grid
              key={balanceField.id}
              container
              spacing={0}
              borderBottom={"1px solid #EAECF0"}
              p={1.5}
            >
              <Grid item xs={3}>
                <Typography flexBasis={"50%"}>
                  {balanceField.name}{" "}
                  <Typography
                    component={"span"}
                    color={"textSecondary"}
                    fontSize={"14px"}
                  >
                    {balanceField.is_discount
                      ? `(${settingsT("isDiscount")})`
                      : ""}
                  </Typography>
                </Typography>
              </Grid>
              {/* quantity */}
              <Grid item xs={3}>
                <Controller
                  name={`balance.${index}.quantity`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      type="number"
                      autoFocus={index === 0}
                      sx={{
                        "& fieldset": {
                          border: "none",
                        },
                        "& .Mui-focused": {
                          border: "1px solid #EAECF0",
                          borderRadius: "7px",
                        },
                      }}
                      variant="outlined"
                      {...field}
                      error={
                        errors.balance && !!errors.balance[index]?.quantity
                      }
                      helperText={errors.balance?.[index]?.quantity?.message}
                      fullWidth
                      size="small"
                      InputProps={{
                        inputProps: { min: 0 },
                        // readOnly:
                        //   !isOneDayFoodicsJourney &&
                        //   watch("journey_type") === "foodics",
                      }}
                      onKeyDown={handleKeyDown}
                      disabled={
                        (!isOneDayFoodicsJourney &&
                          watch("journey_type") === "foodics") ||
                        (isOnboarding && watch("journey_type") === "manual")
                      }
                    />
                  )}
                />
              </Grid>
              {/* amount */}
              <Grid item xs={3}>
                <Controller
                  name={`balance.${index}.amount`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      type="number"
                      variant="outlined"
                      sx={{
                        "& fieldset": {
                          border: "none",
                        },
                        "& .Mui-focused": {
                          border: "1px solid #EAECF0",
                          borderRadius: "7px",
                        },
                      }}
                      {...field}
                      error={errors.balance && !!errors.balance[index]?.amount}
                      helperText={errors.balance?.[index]?.amount?.message}
                      fullWidth
                      size="small"
                      InputProps={{
                        inputProps: { min: 0 },
                        startAdornment: (
                          <InputAdornment position="start">
                            {matchesSMUp ? generalT("sar") : ""}
                          </InputAdornment>
                        ),
                        // readOnly:
                        //   !isOneDayFoodicsJourney &&
                        //   watch("journey_type") === "foodics",
                      }}
                      onKeyDown={handleKeyDown}
                      disabled={
                        (!isOneDayFoodicsJourney &&
                          watch("journey_type") === "foodics") ||
                        (isOnboarding && watch("journey_type") === "manual")
                      }
                    />
                  )}
                />
              </Grid>
              <Grid item xs={3}>
                {/* Start Balance */}
                {mode === "edit" ||
                (watch("journey_type") === "manual" && !isOnboarding) ||
                (watch("journey_type") === "foodics" &&
                  !isOnboarding &&
                  !balanceField.is_new_payment) ? null : (
                  <Controller
                    name={`balance.${index}.start_balance`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        type="number"
                        variant="outlined"
                        sx={{
                          "& fieldset": {
                            border: "none",
                          },
                          "& .Mui-focused": {
                            border: "1px solid #EAECF0",
                            borderRadius: "7px",
                          },
                        }}
                        {...field}
                        error={
                          errors.balance &&
                          !!errors.balance[index]?.start_balance
                        }
                        helperText={
                          errors.balance?.[index]?.start_balance?.message
                        }
                        fullWidth
                        size="small"
                        InputProps={{
                          inputProps: { min: 0 },
                          startAdornment: (
                            <InputAdornment position="start">
                              {matchesSMUp ? generalT("sar") : ""}
                            </InputAdornment>
                          ),
                        }}
                        onKeyDown={handleKeyDown}
                      />
                    )}
                  />
                )}
              </Grid>
            </Grid>
          ))}
          {/* discount payments */}
          {journeyData?.discount_payments.map((disPayment: any) => (
            <Grid
              key={disPayment.id}
              container
              spacing={0}
              borderBottom={"1px solid #EAECF0"}
              p={1.5}
            >
              <Grid item xs={3}>
                <Typography flexBasis={"50%"}>
                  {disPayment.paymentMethod?.name} <br />
                  <Typography
                    component={"span"}
                    color={"textSecondary"}
                    fontSize={"14px"}
                  >
                    {settingsT("isDiscount")}
                  </Typography>
                </Typography>
              </Grid>
              {/* quantity */}
              <Grid item xs={3}>
                <TextField
                  type="number"
                  sx={{
                    "& fieldset": {
                      border: "none",
                    },
                    "& .Mui-focused": {
                      border: "1px solid #EAECF0",
                      borderRadius: "7px",
                    },
                  }}
                  variant="outlined"
                  fullWidth
                  size="small"
                  disabled
                  value={disPayment.quantity}
                />
              </Grid>
              {/* amount */}
              <Grid item xs={3}>
                <TextField
                  type="number"
                  sx={{
                    "& fieldset": {
                      border: "none",
                    },
                    "& .Mui-focused": {
                      border: "1px solid #EAECF0",
                      borderRadius: "7px",
                    },
                  }}
                  variant="outlined"
                  fullWidth
                  size="small"
                  disabled
                  value={disPayment.total}
                />
              </Grid>
              {/* <Grid item xs={3}></Grid> */}
            </Grid>
          ))}
          {getValues("mode") === "add" &&
            getValues("journey_type") === "manual" &&
            getValues("discountPayments").map((disPayment) => (
              <Grid
                key={disPayment.payment_method_id}
                container
                spacing={0}
                borderBottom={"1px solid #EAECF0"}
                p={1.5}
              >
                <Grid item xs={3}>
                  <Typography flexBasis={"50%"}>
                    {disPayment.name} <br />
                    <Typography
                      component={"span"}
                      color={"textSecondary"}
                      fontSize={"14px"}
                    >
                      {settingsT("isDiscount")}
                    </Typography>
                  </Typography>
                </Grid>
                {/* quantity */}
                <Grid item xs={3}>
                  <TextField
                    type="number"
                    sx={{
                      "& fieldset": {
                        border: "none",
                      },
                      "& .Mui-focused": {
                        border: "1px solid #EAECF0",
                        borderRadius: "7px",
                      },
                    }}
                    variant="outlined"
                    fullWidth
                    size="small"
                    disabled
                    value={0}
                  />
                </Grid>
                {/* amount */}
                <Grid item xs={3}>
                  <TextField
                    type="number"
                    sx={{
                      "& fieldset": {
                        border: "none",
                      },
                      "& .Mui-focused": {
                        border: "1px solid #EAECF0",
                        borderRadius: "7px",
                      },
                    }}
                    variant="outlined"
                    fullWidth
                    size="small"
                    disabled
                    value={disPayment.amount}
                  />
                </Grid>
              </Grid>
            ))}
          <Grid
            container
            spacing={3}
            borderBottom={"1px solid #EAECF0"}
            p={1.5}
          >
            <Grid item xs={3}>
              <Typography fontWeight={700}>{t("totalPayments")}</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography color={balanceQntyMatch ? "black" : "error"}>
                {formatNumber(accumulatedBalance?.quantity)}
              </Typography>
            </Grid>

            <Grid item xs={3}>
              <Typography color={balanceAmountMatch ? "black" : "error"}>
                {matchesSMUp ? generalT("sar") : ""}{" "}
                {formatNumber(+accumulatedBalance?.amount.toFixed(2))}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            spacing={3}
            borderBottom={"1px solid #EAECF0"}
            p={1.5}
          >
            <Grid item xs={3}>
              <Typography fontWeight={700}>
                {generalT("netSalesWithTax")}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography>{formatNumber(totalQuantity)}</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography>
                {matchesSMUp ? generalT("sar") : ""}{" "}
                {formatNumber(+totalNetSalesWithTax?.toFixed(2))}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Box>
      {!balanceAmountMatch && (
        <Box mt={3}>
          <InfoBox variant="error" text={t("totalPaymentsMethodsMust")} />
        </Box>
      )}
      {!balanceQntyMatch && (
        <Box mt={3}>
          <InfoBox variant="error" text={t("totalPaymentsQntyMust")} />
        </Box>
      )}
    </Box>
  );
};

export default MakeBalance;
